import type { IChatSharedNotificationData } from "@frontend-shared/types";
import { MessageSquareShare } from "lucide-react";
import { memo, useCallback } from "react";
import { Trans, useTranslation } from "react-i18next";

import { NotificationCardShell } from "../NotificationCardShell";

interface IChatSharedCardProps {
  uuid: string;
  data: IChatSharedNotificationData;
  createdAt: string;
  isRead: boolean;
  onViewSharedChat: (shareToken: string) => void;
}

const ChatSharedCardComponent = ({
  uuid,
  data,
  createdAt,
  isRead,
  onViewSharedChat,
}: IChatSharedCardProps): React.JSX.Element => {
  const { t } = useTranslation("notifications");

  const handleViewSharedChat = useCallback(
    () => onViewSharedChat(data.shareToken),
    [onViewSharedChat, data.shareToken],
  );

  return (
    <NotificationCardShell
      uuid={uuid}
      variantTestId="chat_shared"
      createdAt={createdAt}
      isRead={isRead}
      icon={<MessageSquareShare className="h-5 w-5 text-indigo-500" />}
      iconBgClassName="bg-indigo-500/10"
      ctaLabel={t("view_shared_chat")}
      onCtaClick={handleViewSharedChat}
      body={
        <Trans
          ns="notifications"
          i18nKey="type.chat_shared.body"
          values={{
            ownerName: data.ownerName,
            chatTitle: data.chatTitle,
          }}
          // NOTE: NSAI-6124 — these values are another user's input and `<Trans>`
          // parses its result as markup (`Bob<p>` became an element; a
          // `<b style="x">` merged attributes into the mapped component and
          // threw). Escaping them before the parse and unescaping the text
          // afterwards keeps them literal; only the copy's own tags render.
          tOptions={{ interpolation: { escapeValue: true } }}
          shouldUnescape
          components={{ b: <strong /> }}
        />
      }
    />
  );
};

export const ChatSharedCard = memo(ChatSharedCardComponent);
